import { config } from '../config';
import type { GameMapId } from '../game/maps';
import { createGoogleProvider } from './google';
import { createMapillaryProvider } from './mapillary';
import { createPanoramaxProvider } from './panoramax';
import type { PanoramaProvider, ProviderId } from './types';

/** Providers in order of preference (the most complete coverage first). */
export const PROVIDERS: PanoramaProvider[] = [createGoogleProvider(), createMapillaryProvider(), createPanoramaxProvider()];

const PROVIDER_IDS: ProviderId[] = PROVIDERS.map((p) => p.id);

export function isProviderId(value: unknown): value is ProviderId {
  return typeof value === 'string' && (PROVIDER_IDS as string[]).includes(value);
}

export function getProvider(id: ProviderId): PanoramaProvider {
  const provider = PROVIDERS.find((p) => p.id === id);
  if (!provider) throw new Error(`Source inconnue : ${id}`);
  return provider;
}

export function configuredProviders(): PanoramaProvider[] {
  return PROVIDERS.filter((p) => p.isConfigured());
}

/** Default source: VITE_DEFAULT_PROVIDER when usable, else the first configured one. */
export function defaultProviderId(): ProviderId {
  if (isProviderId(config.defaultProvider) && getProvider(config.defaultProvider).isConfigured()) return config.defaultProvider;
  return configuredProviders()[0]?.id ?? 'panoramax';
}

/** Other usable providers for a map, in order of preference. */
export function fallbackProviders(current: ProviderId, mapId: GameMapId): PanoramaProvider[] {
  return PROVIDERS.filter((p) => p.id !== current && p.isConfigured() && p.supportedMaps.includes(mapId));
}

export type { PanoramaProvider, ProviderId } from './types';
